import { useState, useEffect, useCallback } from "react";
import adminService from "../../api/services/adminService";
import clientService from "../../api/services/userService";

const INITIAL_NOTIFICATION = {
  title: "",
  message: "",
  userType: "",
  country: "",
  phoneVerified: false,
  profileCompletion: false,
  lastActive: false,
  isScheduled: false,
  scheduledDate: "",
  scheduledTime: "",
  userId: null,
  userSearchQuery: "",
};

export function useNotification() {
  const [activeTab, setActiveTab] = useState("send");
  const [newNotification, setNewNotification] = useState(INITIAL_NOTIFICATION);
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [historyFilters, setHistoryFilters] = useState({
    type: "",
    category: "",
    startDate: "",
    endDate: "",
  });

  const fetchNotificationHistory = useCallback(async (page = 1, filters = {}) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const params = { page, limit: 20, ...filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) delete params[key];
      });
      const response = await adminService.getNotificationHistory(params);
      if (response.success && response.notifications) {
        setNotificationHistory(response.notifications);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
          setItemsPerPage(response.pagination.itemsPerPage);
        }
      }
    } catch (err) {
      console.error("Error fetching notification history:", err);
      setHistoryError(err?.message || "Failed to fetch notification history");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await adminService.getDistinctCountries();
        if (response.success && response.countries) {
          setCountries(response.countries);
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchNotificationHistory(currentPage, historyFilters);
    }
  }, [
    activeTab,
    currentPage,
    historyFilters.type,
    historyFilters.category,
    historyFilters.startDate,
    historyFilters.endDate,
    fetchNotificationHistory,
  ]);

  const handleSearchUser = useCallback(async () => {
    if (!newNotification.userSearchQuery.trim()) {
      setError("Please enter an email or contact number to search");
      return;
    }
    setSearchingUser(true);
    setError("");
    setSearchedUser(null);
    try {
      const query = newNotification.userSearchQuery.trim();
      const isEmail = query.includes("@");
      const response = isEmail
        ? await clientService.fetchUserByEmail(query)
        : await adminService.searchUserForPromotionalMessage(null, query);
      if (response.responseCode === 200 && response.user) {
        setSearchedUser(response.user);
        setNewNotification((prev) => ({ ...prev, userId: response.user.id }));
      } else {
        setError("User not found. Please check the email or contact number.");
      }
    } catch (err) {
      console.error("Error searching user:", err);
      setError(err?.message || "Failed to search user. Please try again.");
    } finally {
      setSearchingUser(false);
    }
  }, [newNotification.userSearchQuery]);

  const handleCreateNotification = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setSuccessMsg("");
      setLoading(true);

      if (newNotification.userType === "single-user" && !newNotification.userId) {
        setError("Please search and select a user first");
        setLoading(false);
        return;
      }
      if (
        newNotification.userType === "single-user" &&
        newNotification.isScheduled
      ) {
        setError(
          "Scheduling is not available for single user notifications. Please uncheck the scheduling option."
        );
        setLoading(false);
        return;
      }

      let scheduled_at = null;
      if (
        newNotification.userType !== "single-user" &&
        newNotification.isScheduled &&
        newNotification.scheduledDate &&
        newNotification.scheduledTime
      ) {
        const scheduledDateTime = new Date(
          `${newNotification.scheduledDate}T${newNotification.scheduledTime}`
        );
        if (scheduledDateTime <= new Date()) {
          setError("Scheduled time must be in the future");
          setLoading(false);
          return;
        }
        scheduled_at = scheduledDateTime.toISOString();
      }

      const payload = {
        title: newNotification.title,
        message: newNotification.message,
        userType:
          newNotification.userType === "single-user"
            ? "single-user"
            : newNotification.userType || null,
        userId:
          newNotification.userType === "single-user"
            ? newNotification.userId
            : null,
        country:
          newNotification.userType === "single-user"
            ? null
            : newNotification.country === "all" || !newNotification.country
            ? null
            : newNotification.country,
        phoneVerified:
          newNotification.userType === "single-user"
            ? null
            : newNotification.phoneVerified || null,
        profileCompletion:
          newNotification.userType === "single-user"
            ? null
            : newNotification.profileCompletion || null,
        lastActive:
          newNotification.userType === "single-user"
            ? null
            : newNotification.lastActive || null,
        scheduled_at,
      };

      try {
        const response = await adminService.sendFilteredNotification(payload);
        let successMessage = "";
        if (newNotification.userType === "single-user") {
          successMessage = `Notification sent successfully to ${
            searchedUser?.firstName || "user"
          } ${searchedUser?.lastName || ""}!`;
        } else if (scheduled_at) {
          successMessage = `Notification scheduled successfully for ${new Date(
            scheduled_at
          ).toLocaleString()}!`;
        } else {
          successMessage = `Notification sent successfully! Delivered to ${
            response.totalUsers || 0
          } users (${response.successCount || 0} successful, ${
            response.failedCount || 0
          } failed).`;
        }
        setSuccessMsg(successMessage);
        setNewNotification(INITIAL_NOTIFICATION);
        setSearchedUser(null);
        if (activeTab === "history") {
          fetchNotificationHistory(currentPage, historyFilters);
        }
      } catch (err) {
        console.error("Create notification error:", err);
        if (err?.message?.includes("Network error")) {
          setError(
            "Network Error: Cannot connect to server. Please check your connection and try again."
          );
        } else if (err?.message) {
          setError(`Error: ${err?.message}`);
        } else if (err?.data) {
          setError(`Server Error: ${JSON?.stringify(err?.data)}`);
        } else {
          setError("Failed to send notification. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [
      newNotification,
      searchedUser,
      activeTab,
      currentPage,
      historyFilters,
      fetchNotificationHistory,
    ]
  );

  const handleFilterChange = useCallback((key, value) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (historyFilters.type) count++;
    if (historyFilters.category) count++;
    if (historyFilters.startDate) count++;
    if (historyFilters.endDate) count++;
    return count;
  }, [historyFilters]);

  const getFilterLabel = useCallback((key, value) => {
    if (key === "type") {
      const typeLabels = {
        VISIT: "Visit",
        COMPLIMENT: "Compliment",
        PROPOSAL: "Proposal",
        ADMIN_FILTERED_NOTIFICATION: "All Filtered User",
        ADMIN_SINGLE_USER_NOTIFICATION: "Single User",
        WELCOME: "Welcome",
        PROMOTIONAL_MESSAGE: "Promotional Message",
        POST_LIKE: "Post Like",
        POST_SHARE: "Post Share",
        POST_COMMENT: "Post Comment",
        POST_REPLY: "Post Reply",
      };
      return typeLabels[value] || value;
    }
    if (key === "category") {
      const categoryLabels = { post: "Post related", other: "Other" };
      return categoryLabels[value] || value;
    }
    if (key === "startDate" || key === "endDate") {
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return value;
  }, []);

  const removeFilter = useCallback((key) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: "" }));
    setCurrentPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setHistoryFilters({ type: "", category: "", startDate: "", endDate: "" });
    setCurrentPage(1);
  }, []);

  const setNotificationField = useCallback((field, value) => {
    setNewNotification((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setUserType = useCallback((newUserType) => {
    if (newUserType === "single-user") {
      setNewNotification((prev) => ({
        ...prev,
        userType: newUserType,
        country: "",
        phoneVerified: false,
        profileCompletion: false,
        lastActive: false,
        isScheduled: false,
        scheduledDate: "",
        scheduledTime: "",
      }));
    } else {
      setNewNotification((prev) => ({
        ...prev,
        userType: newUserType,
        userId: null,
        userSearchQuery: "",
      }));
      setSearchedUser(null);
    }
  }, []);

  return {
    activeTab,
    setActiveTab,
    newNotification,
    setNewNotification,
    setNotificationField,
    setUserType,
    searchedUser,
    setSearchedUser,
    searchingUser,
    countries,
    loading,
    loadingCountries,
    error,
    setError,
    successMsg,
    notificationHistory,
    loadingHistory,
    historyError,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    historyFilters,
    handleSearchUser,
    handleCreateNotification,
    handleFilterChange,
    getActiveFiltersCount,
    getFilterLabel,
    removeFilter,
    clearAllFilters,
    fetchNotificationHistory,
  };
}
